import React from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Tag, CalendarDays, ArrowRight } from "lucide-react";

function getPrimaryImage(item) {
  if (Array.isArray(item.photos) && item.photos.length > 0) return item.photos[0];
  return item.imageUrl || item.image || "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?auto=format&fit=crop&w=1200&q=80";
}

function getName(item) {
  return item.title || item.name || "Untitled merch";
}

function getAssociatedEvents(item, events) {
  const associatedIds = Array.isArray(item.associatedEvents)
    ? item.associatedEvents
    : item.eventId
    ? [item.eventId]
    : [];

  return events.filter((event) => associatedIds.includes(event._id || event.id));
}

export default function MerchFeed({ merch = [], events = [] }) {
  const activeMerch = merch.filter((item) => item.active !== false);

  return (
    <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <header className="mb-10 max-w-3xl">
        <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm ring-1 ring-slate-200">
          <ShoppingBag className="h-4 w-4" />
          Merch marketplace
        </div>
        <h1 className="mt-4 text-4xl font-semibold tracking-tight text-slate-900">
          Shop merch on and off the event card.
        </h1>
        <p className="mt-3 text-base leading-7 text-slate-600">
          Browse merchandise sold by promoters and artists, including items tied to specific shows.
        </p>
      </header>

      {activeMerch.length === 0 ? (
        <div className="rounded-[2rem] border border-slate-200 bg-white px-8 py-16 text-center shadow-sm">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
            No merch available yet
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">
            As sellers start adding products, they’ll appear here and can also be linked directly to their events.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {activeMerch.map((item) => {
            const linkedEvents = getAssociatedEvents(item, events);

            return (
              <Link
                key={item._id || item.id}
                to={`/merch/${item._id || item.id}`}
                className="group block rounded-[2rem] focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-200"
              >
                <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
                  <div className="relative h-72 overflow-hidden bg-slate-100">
                    <img
                      src={getPrimaryImage(item)}
                      alt={getName(item)}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                    />
                    <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                      <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-800 shadow-sm backdrop-blur-sm">
                        ${(Number(item.price) || 0).toFixed(2)}
                      </span>
                      {linkedEvents.length > 0 && (
                        <span className="rounded-full bg-slate-900/75 px-3 py-1 text-xs font-medium text-white shadow-sm backdrop-blur-sm">
                          {linkedEvents.length} linked event{linkedEvents.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h2 className="text-xl font-semibold tracking-tight text-slate-900">
                          {getName(item)}
                        </h2>
                        <p className="mt-1 text-sm text-slate-500">
                          {item.sellerName || item.bandName || item.artistName || "Independent seller"}
                        </p>
                      </div>
                      <Tag className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
                    </div>

                    <p className="mt-4 line-clamp-3 text-sm leading-6 text-slate-600">
                      {item.description || "Merchandise available for purchase through OnStage."}
                    </p>

                    {linkedEvents.length > 0 && (
                      <div className="mt-5 rounded-2xl bg-slate-50 p-4">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <CalendarDays className="h-3.5 w-3.5" />
                          Linked events
                        </div>
                        <p className="mt-2 line-clamp-2 text-sm text-slate-700">
                          {linkedEvents.slice(0, 2).map((event) => event.name).join(" • ")}
                          {linkedEvents.length > 2 ? ` +${linkedEvents.length - 2} more` : ""}
                        </p>
                      </div>
                    )}

                    <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
                      <span className="text-sm font-medium text-slate-900 transition-colors group-hover:text-slate-700">
                        View merch
                      </span>
                      <ArrowRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-1 group-hover:text-slate-600" />
                    </div>
                  </div>
                </article>
              </Link>
            );
          })}
        </div>
      )}
    </main>
  );
}
